import React, { useEffect, useMemo, useState } from 'react';
import { useAppContext } from '../../Services/AppContext';
import moment, { Moment } from 'moment';
import TimePicker from '../Base/TimePicker/TimePicker';
import DatePicker from '../Base/DatePicker/DatePicker';
import RadioList from '../Base/RadioList/RadioList';
import Checkbox from '../Base/Checkbox/Checkbox';
import TextField from '../Base/TextField/TextField';
import FormControl from '../Form/FormControl';
import SubmitButton from '../Button/SubmitButton';
import apiClient from '../../Services/ApiClient';
import Form from '../Form/Form';
import { WORKPLACE_DOCTORS_QUERY } from '../../Modules/Doctor/Graphql/workplace-doctors.query';
import { useEffectAsync } from '../../helpers';
import MultiSelect from '../Base/MultiSelect/MultiSelect';
import withForm from '../Form/withForm';
import useNotification from '../../Hooks/useNotification.hook';
import { useFormContext } from '../Form/FormContext';
import Select from '../Base/Select/Select';
import { buildScheduleItem, calculateTimesFromValues } from './calendar.helper';
import SubmitButtonWithConfirmation from '../Button/SubmitButtonWithConfirmation';
import { Theme } from '../../Enums/Theme.enum';
import * as yup from 'yup';
import useTranslation from '../../Hooks/useTranslation';
import FullPageLoading from '../Utils/FullPageLoading/FullPageLoading';
import { getMomentDate } from '../../Utils/dates.util';
import { checkIsTimeSameOrAfter } from '../../Modules/Events/Services/eventInterval.service';
import FormattedPriceInputField from '../../Modules/Base/Components/Inputs/FormattedPriceInput/FormattedPriceInputField';
import Text from '../Base/Text/Text';
import LinkButton from '../Button/LinkButton';
import { useInputValidation } from '../../Modules/Base/Hooks/useInputValidation';
import useTimezoneInfo from '../../Modules/Base/Hooks/useTimezoneInfo';
import { getTimezonesDifference } from '../../Modules/Calendar/Utils/getTimezonesDifference';
import TimeZoneInfo from '../TimeZoneInfo/TimeZoneInfo';
import CalendarTimezoneWarning from './CalendarTimezoneWarning/CalendarTimezoneWarning';
import { getFormattedPriceAsFloat } from '../../Modules/Base/Services/priceFormat.service';
import {
	useLazyMetaQuery,
	useLazyWorkplaceDoctorsQuery,
} from '../../graphql/graphql';
import useWorkplaceEvents from '../../Modules/Events/Hooks/useWorkplaceEvents';

interface CalendarWorkplaceScheduleEventProps {
	selectedDate: Moment;
	onSuccess?: () => void;
	workplaceId?: string;
	schedule?: any;
	onDelete: () => void;
}

const CalendarWorkplaceScheduleEvent: React.FC<
	CalendarWorkplaceScheduleEventProps
> = props => {
	const { selectedDate, onSuccess, workplaceId, schedule, onDelete } = props;
	const { translation, currency, vatRate, isLatam, defaultTimeZone } =
		useAppContext();
	const loggedUserTimeZone = Intl.DateTimeFormat()?.resolvedOptions()?.timeZone;
	const [data, setData] = useState({});
	const [individualTime, setIndividualTime] = useState(false);
	const [extraService, setExtraService] = useState(false);
	const [extraPayment, setExtraPayment] = useState(false);
	const [searchDoctors, setSearchDoctors] = useState([]);
	const [workingHours, setWorkingHours] = useState(null);
	const [workingHoursExtra, setWorkingHoursExtra] = useState(null);
	const [eventTypes, setEventTypes] = useState([]);
	const [workplaceMembers, setWorkplaceMembers] = useState([]);
	const [initialized, setInitialized] = useState(true);
	const [defaultData, setDefaultData] = useState(null);
	const [showRepeatTo, setShowRepeatTo] = useState(false);
	const { formMethods } = useFormContext();
	const [pickedDate, setPickedDate] = useState(
		schedule ? moment(schedule.event.start) : moment(selectedDate).format(),
	);
	const [isLoading, setIsLoading] = useState(false);
	const { t } = useTranslation();
	const aa = null;
	const { getPriceInputValidation } = useInputValidation();
	const [getWorkplaceDoctors] = useLazyWorkplaceDoctorsQuery();
	const [getMeta] = useLazyMetaQuery();
	const { getWorkersAbsenceEvents } = useWorkplaceEvents();

	const currencyCodeKey = 'base.currencyCode.' + currency;
	//  const workingTime = watch('workingTime');
	const { showNotificationAlert, showNotificationSuccess } = useNotification();
	const {
		timeFrom,
		timeTo,
		handleGetPatientsTimes,
		// @ts-ignore
	} = useTimezoneInfo(defaultData?.timeFrom, defaultData?.timeTo);

	const { diffInMinutes, formattedDiff } = useMemo(
		() =>
			getTimezonesDifference({
				timezone1: loggedUserTimeZone,
				timezone2: defaultTimeZone,
			}),
		[loggedUserTimeZone, defaultTimeZone],
	);

	useEffectAsync(async () => {
		await setInitialized(false);
		await setInitialized(true);
	}, [defaultData]);

	useEffectAsync(async () => {
		await loadWorkers();
	}, [pickedDate]);

	useEffectAsync(async () => {
		await loadWorkers();
	}, []);

	const loadWorkers = async () => {
		const events = await getWorkersAbsenceEvents({
			dateFrom: moment(pickedDate)
				.clone()
				.startOf('day')
				.add('00', 'hours')
				.add('00', 'minutes'),
			dateTo: moment(pickedDate)
				.clone()
				.startOf('day')
				.add('23', 'hours')
				.add('59', 'minutes'),
			workplaceIds: [workplaceId],
		});

		const absence = [];

		for (const event of events || []) {
			absence.push({
				from: event.start,
				to: event.end,
				label: event.shift?.label,
				userId: event.doctor?.id,
				userName: event.doctor?.name,
			});
		}

		const tempWorkplaceMembers = [];

		const workplaceDoctors = await getWorkplaceDoctors({
			workplaceId,
			roleKeys: ['doctor', 'nurse', 'therapist'],
			take: 999,
		});

		for (const workplaceDoctor of workplaceDoctors?.data?.workplaceDoctors
			?.data || []) {
			let label = workplaceDoctor?.user?.profile?.fullName;
			let hasAbsence = false;

			const findAbsence = absence.find(
				w => w.userId === workplaceDoctor.user?.id,
			);
			if (findAbsence) {
				label =
					workplaceDoctor?.user?.profile?.fullName +
					' (' +
					findAbsence.label +
					')';
				hasAbsence = true;
			}
			tempWorkplaceMembers.push({
				value: workplaceDoctor.id,
				hasAbsence,
				label,
			});
		}
		setWorkplaceMembers(
			tempWorkplaceMembers.sort((a, b) => (a.hasAbsence ? 1 : -1)),
		);
	};

	useEffectAsync(async () => {
		let tempEventTypes = [];

		const tempWH = await handleCalcWorkingHours();

		if (tempWH) {
			tempEventTypes.push({
				value: 'SHIFT',
				title: `${
					translation['base.shared.officeHours']
				} (${handleCalcWorkingHour(tempWH.from).format(
					'HH:mm',
				)} - ${handleCalcWorkingHour(tempWH.to).format('HH:mm')})`,
			});
			setWorkingHours({
				from: handleCalcWorkingHour(tempWH.from),
				to: handleCalcWorkingHour(tempWH.to),
			});
		}

		const tempWHE = await handleCalcWorkingHours(true);
		if (tempWHE) {
			tempEventTypes.push({
				value: 'SHIFT_EXTRA',
				title: `${
					translation['base.extraSwiftSlashPayedExtraShift']
				} (${handleCalcWorkingHour(tempWHE.from).format(
					'HH:mm',
				)} - ${handleCalcWorkingHour(tempWHE.to).format('HH:mm')})`,
			});
			setWorkingHoursExtra({
				from: handleCalcWorkingHour(tempWHE.from),
				to: handleCalcWorkingHour(tempWHE.to),
			});
		}

		if (schedule) {
			tempEventTypes = tempEventTypes.filter(
				t => t.key === schedule.event.extendedProps.shift.type,
			);
			setExtraPayment(
				schedule.event.extendedProps?.serviceScheduleItem?.price
					?.priceWithVatRaw > 0,
			);
		}

		if (!individualTime && schedule) {
			let timeFromLoaded;
			let timeToLoaded;

			if (schedule.event.extendedProps.shift.type === 'SHIFT') {
				if (tempWH) {
					timeFromLoaded = handleCalcWorkingHour(tempWH.from);
					timeToLoaded = handleCalcWorkingHour(tempWH.to);
				}
			} else if (schedule.event.extendedProps.shift.type === 'SHIFT_EXTRA') {
				if (tempWHE) {
					timeFromLoaded = handleCalcWorkingHour(tempWHE.from);
					timeToLoaded = handleCalcWorkingHour(tempWHE.to);
				}
			}

			if (
				moment(schedule.event.start).format('HH:mm') != timeFromLoaded ||
				moment(schedule.event.end).format('HH:mm') != timeToLoaded
			) {
				setIndividualTime(true);
			}

			const item = schedule.event.extendedProps.serviceScheduleItem;
			if (item.price) {
				setExtraPayment(true);
			}
		}

		setEventTypes(tempEventTypes);
	}, [pickedDate]);

	const handleCalcWorkingHour = time => {
		const tempSelectedDate = schedule
			? moment(schedule.event.start)
			: getMomentDate(selectedDate);

		const timeFromParts = time.split(':');
		const timeFrom = tempSelectedDate.clone().set({
			hour: timeFromParts[0],
			minute: timeFromParts[1],
		});

		return moment(timeFrom);
	};

	const handleCalcWorkingHours = async (extra = false) => {
		const workingHours = await getMeta({
			key: 'workingHours' + (extra ? 'Extra' : ''),
			workplaceId,
		});

		const dayIndex = getMomentDate(pickedDate).day();

		const scheduleCheck = workingHours?.data?.meta?.value;

		if (scheduleCheck) {
			const dayIndexes = {
				1: 'mon',
				2: 'tue',
				3: 'wed',
				4: 'thu',
				5: 'fri',
				6: 'sat',
				7: 'sun',
				0: 'sun',
			};

			const workingHoursArray = scheduleCheck[dayIndexes[dayIndex]] || null;

			if (workingHoursArray) {
				let lowest = null;
				let max = null;
				workingHoursArray.map(wh => {
					const i = Number(wh.timeFrom.split(':')[0]);
					if (!lowest || (lowest && Number(lowest.split(':')[0]) > i)) {
						lowest = wh.timeFrom;
					}
					const k = Number(wh.timeTo.split(':')[0]);
					if (!max || (max && Number(max.split(':')[0]) < k)) {
						max = wh.timeTo;
					}
				});

				return {
					from: lowest,
					to: max,
				};
			}
		}
	};

	const days = {
		Monday: 'mon',
		Tuesday: 'tue',
		Wednesday: 'wed',
		Thursday: 'thu',
		Friday: 'fri',
		Saturday: 'sat',
		Sunday: 'sun',
	};

	const handleRemove = async function () {
		await apiClient.delete(
			'/workplaces/' +
				workplaceId +
				'/services-schedule/' +
				schedule.event.extendedProps.scheduleId +
				'/items/' +
				schedule.event.extendedProps.scheduleItemId,
		);
		onSuccess?.();
	};

	// @ts-expect-error
	const handleUpdate = (values, updateFuture = false) => {
		const a = async () => {
			values.date = getMomentDate(values.date);

			let { date } = values;

			const { timeFrom, timeTo, dateFrom, dateTo } = calculateTimesFromValues({
				...values,
				workingHours,
				workingHoursExtra,
				individualTime,
			});

			const membersToSave = [];

			for (const workplaceDoctor of values.workplaceMembers || []) {
				membersToSave.push(workplaceDoctor.value);
			}

			const updateData = {
				day: days[date.format('dddd')],
				timeFrom: moment(timeFrom).format('HH:mmZ'),
				timeTo: moment(timeTo).format('HH:mmZ'),
				workplaceMemberIds: membersToSave,
				canOrderImmediate: true,
				price:
					values.price && extraPayment
						? {
								priceWithVat: getFormattedPriceAsFloat(values.price),
								currency,
								vatRate,
						  }
						: null,
				interval: values.guaranteedTime || null,
				dateFrom,
				dateTo,
			};

			const { repeatable } = schedule.event.extendedProps;

			if (repeatable) {
				updateData.updateFuture = updateFuture;
			} else {
				if (values.repeatInterval && values.repeatInterval.value != 'never') {
					updateData.repeatInterval = values.repeatInterval.value;
					updateData.repeatTo = getMomentDate(values.repeatTo).format(
						'YYYY-MM-DD',
					);
				}
			}

			await apiClient.patch(
				'/workplaces/' +
					workplaceId +
					'/services-schedule/' +
					schedule.event.extendedProps.scheduleId +
					'/items/' +
					schedule.event.extendedProps.scheduleItemId,
				updateData,
			);

			onSuccess?.();
		};

		a();
	};

	// @ts-expect-error
	const handleCreate = async function (values) {
		if (eventTypes.length === 0 && !values.individualTime) {
			return showNotificationAlert({
				content: translation['component.workplaceSettings.workingTimeRequired'],
			});
		}
		setIsLoading(true);
		values.date = getMomentDate(values.date);

		const { dateFrom, dateTo, timeFrom, timeTo } = calculateTimesFromValues({
			...values,
			workingHours,
			workingHoursExtra,
			individualTime,
		});

		try {
			const serviceSchedule = await apiClient.post(
				'/workplaces/' + workplaceId + '/services-schedule/',
				{
					dateFrom: dateFrom.format(),
					dateTo: dateTo.format(),
					form: 'standard',
					type: values.workingTime.toLowerCase(),
				},
			);

			if (serviceSchedule) {
				const scheduleId = serviceSchedule.data.id;
				const preparedObject = buildScheduleItem({
					...values,
					timeTo,
					timeFrom,
					price:
						values.price && extraPayment
							? {
									priceWithVat: getFormattedPriceAsFloat(values.price),
									currency,
									vatRate,
							  }
							: null,
				});
				const serviceScheduleItems = await apiClient.post(
					'/workplaces/' +
						workplaceId +
						'/services-schedule/' +
						scheduleId +
						'/items/',
					preparedObject,
				);
				showNotificationSuccess({
					content:
						translation['component.workplaceSettings.scheduleEventCreated'],
				});
				onSuccess?.();
			}
		} catch (e) {
			if (e.response.data.displayableError) {
				showNotificationAlert({
					content: e.response.data.message,
				});
			}
		}
		setIsLoading(false);
	};

	const handleSetData = (key, value) => {
		setData(prev => ({ ...prev, [key]: value }));
	};

	const handleSetExtraPayment = () => {
		setExtraPayment(prevValue => !prevValue);
	};

	const handleExtraService = key => {
		if (key === 'SHIFT_EXTRA') {
			setExtraService(true);
			setExtraPayment(true);
		} else {
			setExtraService(false);
			setExtraPayment(false);
		}
	};

	const handleSearchDoctors = value => {
		const a = async () => {
			setSearchDoctors([]);

			if (!value) {
				return;
			}

			const { meta, data } = await apiClient.graphql({
				query: WORKPLACE_DOCTORS_QUERY,
				variables: {
					fulltext: value,
				},
				fetchPolicy: 'network-only',
			});

			setSearchDoctors(data.workplaceDoctors?.data || []);
		};
		a();
	};

	const defaultWorkplaceMembers = [];

	if (schedule) {
		for (const workplaceDoctor of schedule.event.extendedProps.workers || []) {
			defaultWorkplaceMembers.push({
				value: workplaceDoctor.workplaceMemberId,
				label: workplaceDoctor.name,
			});
		}
	}

	const handleToggleIndividualTime = () => {
		setIndividualTime(prev => !prev);
	};

	useEffect(() => {
		const a = async () => {
			let tempIndividualTime = false;
			if (schedule) {
				const tempWH = await handleCalcWorkingHours();
				const tempWHE = await handleCalcWorkingHours(true);

				let timeFromLoaded;
				let timeToLoaded;

				if (schedule.event.extendedProps.shift.type === 'SHIFT') {
					if (tempWH) {
						timeFromLoaded = handleCalcWorkingHour(tempWH.from);
						timeToLoaded = handleCalcWorkingHour(tempWH.to);
					}
				} else if (schedule.event.extendedProps.shift.type === 'SHIFT_EXTRA') {
					if (tempWHE) {
						timeFromLoaded = handleCalcWorkingHour(tempWHE.from);
						timeToLoaded = handleCalcWorkingHour(tempWHE.to);
					}
				}

				if (
					moment(schedule.event.start).format('HH:mm') != timeFromLoaded ||
					moment(schedule.event.end).format('HH:mm') != timeToLoaded
				) {
					tempIndividualTime = true;
				}
			}

			setDefaultData({
				date: schedule ? moment(schedule.event.start) : selectedDate,
				workplaceMembers: schedule ? defaultWorkplaceMembers : null,
				timeFrom: schedule?.event?.start
					? moment(schedule.event.start, 'HH:mm')
					: moment(selectedDate, 'HH:mm'),
				timeTo: schedule?.event?.end
					? moment(schedule.event.end, 'HH:mm')
					: undefined,
				workingTime: schedule
					? schedule.event.extendedProps.shift.type
					: 'SHIFT',
				individualTime: tempIndividualTime,
				repeatInterval: { value: 'never', label: translation['base.never'] },
				guaranteedTime: schedule
					? schedule.event.extendedProps?.serviceScheduleItem?.interval
					: '',
				price: schedule
					? parseFloat(
							schedule.event.extendedProps?.serviceScheduleItem?.price
								?.priceWithVatRaw || 0,
					  ) || ''
					: '',
				extraPayment: schedule
					? schedule?.event.extendedProps?.serviceScheduleItem?.price
							?.priceWithVatRaw > 0
					: false,
			});
		};

		a().then(_ => {});
	}, [schedule]);

	const handleToggleRepeat = () => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();
		setShowRepeatTo(formValues?.repeatInterval?.value != 'never');
	};

	const handleSelectDate = () => {
		if (!formMethods) {
			return;
		}
		const formValues = formMethods.getValues();
		setPickedDate(moment(formValues.date).format());
	};

	const validationSchema = yup.object({
		date: yup
			.date()
			.required(translation['validation.calendarWorkplaceScheduleEvent.date']),
		workplaceMembers: yup
			.mixed()
			.required(
				translation[
					'validation.calendarWorkplaceScheduleEvent.workplaceMembers'
				],
			),
		repeatInterval: yup
			.mixed()
			.required(
				translation['validation.calendarWorkplaceScheduleEvent.repeatInterval'],
			),
		workingTime: yup
			.mixed()
			.required(
				translation['validation.calendarWorkplaceScheduleEvent.workingTime'],
			),
		individualTime: yup.boolean().notRequired(),
		timeFrom: !individualTime
			? yup.mixed().notRequired()
			: yup
					.date()
					.nullable()
					.default(undefined)
					.required(
						translation['validation.calendarWorkplaceScheduleEvent.timeFrom'],
					),
		timeTo: !individualTime
			? yup.mixed().notRequired()
			: yup
					.date()
					.nullable()
					.default(undefined)
					.test(
						'is-greater',
						t('validation.calendarWorkplaceScheduleEvent.timeDiff') ?? ' ',
						function () {
							const { date, timeFrom, timeTo } = this.parent;
							return checkIsTimeSameOrAfter(date, timeFrom, timeTo);
						},
					)
					.required(
						translation['validation.calendarWorkplaceScheduleEvent.timeTo'],
					),
		guaranteedTime: !extraPayment
			? yup.mixed().notRequired()
			: yup
					.number()
					.typeError(t('validation.form.mustFillNumber'))
					.required(
						translation[
							'validation.calendarWorkplaceScheduleEvent.workingTime'
						],
					),
		price: !extraPayment
			? yup.mixed().notRequired()
			: getPriceInputValidation(
					'validation.calendarWorkplaceScheduleEvent.price',
					true,
			  ),
		repeatTo: yup.mixed().when('repeatInterval', {
			is: ({ value }) => value !== 'never',
			then: yup
				.date()
				.required(
					translation['validation.calendarWorkplaceScheduleEvent.repeatTo'],
				)
				.default(undefined),
		}),
	});

	return (
		<>
			{isLoading && <FullPageLoading />}
			{initialized && (
				<Form defaultValues={defaultData} schema={validationSchema}>
					{!isLatam && (
						<div className='mb-3'>
							<Text
								tx='component.calendarWorkplaceScheduleEvent.setupToWhoCanPatientsMakeOrders'
								style={{ fontWeight: 600 }}
							/>
						</div>
					)}
					<FormControl
						label='base.shared.date'
						name='date'
						onChange={handleSelectDate}
						isRequired>
						<DatePicker />
					</FormControl>
					<FormControl
						label='base.selectWorker'
						name='workplaceMembers'
						isRequired>
						<MultiSelect
							options={workplaceMembers}
							placeholder={translation['base.shared.pick']}
							isSearchable
						/>
					</FormControl>
					{(!schedule || !schedule?.event?.extendedProps?.repeatable) && (
						<div className={'row'}>
							<div className={'col-6'}>
								<FormControl
									label='base.repeat'
									name='repeatInterval'
									onChange={handleToggleRepeat}
									isRequired>
									<Select
										options={[
											{ value: 'never', label: translation['base.never'] },
											{
												value: 'everyWorkingDay',
												label:
													translation[
														'component.calendarWorkplaceScheduleEvent.everyWorkingDay'
													],
											},
											{
												value: 'everyWeek',
												label: translation['base.everyWeek'],
											},
											/*
                                            {
                                                value: 'everyFortnight',
                                                label: translation['base.everyFortnight'],
                                            },
                                            */
											{
												value: 'everyMonth',
												label: translation['base.everyMonth'],
											},
										]}
									/>
								</FormControl>
							</div>
							{showRepeatTo && (
								<div className={'col-6'}>
									<FormControl
										label='base.repeatTo'
										name='repeatTo'
										hideRequired={true}>
										<DatePicker />
									</FormControl>
								</div>
							)}
						</div>
					)}
					<FormControl label='base.workingTime' name='workingTime' isRequired>
						<RadioList data={eventTypes} inLine={true} />
					</FormControl>
					<FormControl
						name={'individualTime'}
						onChange={handleToggleIndividualTime}
						hideRequired={true}>
						<Checkbox tx={'base.pickIndividualTime'} firstUpper={true} />
					</FormControl>

					{individualTime && (
						<>
							{diffInMinutes > 0 && (
								<CalendarTimezoneWarning
									timezone={defaultTimeZone}
									timeDiff={formattedDiff}
									textKeys={{
										part1: 'base.workplaceTimeIsInTimeZone.part1',
										part2: 'base.workplaceTimeIsInTimeZone.part2',
										hourMiddle: 'base.shared.hourMiddle',
									}}
								/>
							)}
							<div className={'row'}>
								<div className={'col-6'}>
									<FormControl
										label='base.shared.from'
										name='timeFrom'
										onChange={() =>
											handleGetPatientsTimes(formMethods.getValues())
										}
										isRequired>
										<TimePicker />
									</FormControl>
									<TimeZoneInfo
										loggedUserTimeZone={loggedUserTimeZone}
										comparedTimeZone={defaultTimeZone}
										time={timeFrom}
									/>
								</div>
								<div className={'col-6'}>
									<FormControl
										label='base.shared.to'
										name='timeTo'
										onChange={() =>
											handleGetPatientsTimes(formMethods.getValues())
										}
										isRequired>
										<TimePicker />
									</FormControl>
									<TimeZoneInfo
										loggedUserTimeZone={loggedUserTimeZone}
										comparedTimeZone={defaultTimeZone}
										time={timeTo}
									/>
								</div>
							</div>
						</>
					)}
					<FormControl name={'extraPayment'} onChange={handleSetExtraPayment}>
						<Checkbox
							tx={'component.caseType.atThisTimePatientHasToPayFee'}
							firstUpper
							value={extraPayment}
						/>
					</FormControl>
					{extraPayment && (
						<>
							<FormControl
								label='component.caseType.guaranteedToConnectIn'
								name='guaranteedTime'
								isRequired>
								<TextField suffix={'base.minutes2'} />
							</FormControl>
							<FormattedPriceInputField
								name='price'
								label='component.caseType.feeAmount'
								isRequired
							/>
						</>
					)}
					<div
						className={'d-flex flex-row align-items-center mt-4'}
						style={{ gap: '10px' }}>
						{schedule ? (
							<>
								{schedule?.event?.extendedProps?.repeatable ? (
									<SubmitButtonWithConfirmation
										title={'base.shared.edit'}
										onSubmit={null}
										confirmationTitle={'base.shared.edit'}
										confirmationText={
											'component.schedule.editMultipleScheduleConfirmationMessage'
										}
										customButtons={() => (
											<React.Fragment>
												<SubmitButton
													onSubmit={handleUpdate}
													title={'base.editOnlyThisOne'}
												/>
												<SubmitButton
													onSubmit={values => handleUpdate(values, true)}
													title={'base.editAlsoFutures'}
												/>
											</React.Fragment>
										)}
									/>
								) : (
									<SubmitButton
										onSubmit={handleUpdate}
										title={'base.shared.edit'}
									/>
								)}
								<LinkButton
									theme={Theme.RED}
									title='base.deleteSchedule'
									onClick={onDelete}
									firstUpper
								/>
							</>
						) : (
							<SubmitButton onSubmit={handleCreate} disabled={isLoading} />
						)}
					</div>
				</Form>
			)}
		</>
	);
};

export default withForm(CalendarWorkplaceScheduleEvent);
